import React, { useMemo } from 'react'
import { Handle, Position } from '@xyflow/react'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { getOvalPoint } from '../utils/getOvalPoint'
import { useEntityGraphContext } from '../../Wrapper/EntityGraphProvider'
import { nodePropTypes } from './constants'
import { useGetNodeValues } from './hooks/useGetNodeValues'

const useStyles = makeStyles(() => ({
  node: ({ size, backgroundColor, borderColor }) => ({
    borderRadius: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: '10px 2em',
    flexDirection: 'column',
    border: 'var(--xy-node-border-default)',
    color: 'var(--xy-node-color, var(--xy-node-color-default))',
    backgroundColor: backgroundColor ?? '#fff',
    height: '100%',
    fontSize: size === 'sm' ? '0.5em' : size === 'lg' ? '1.35em' : '1em',
    gap: '.25em',
    '&.selected': {
      outline: '2px solid var(--color-primary-400)'
    },
    '& .header': {
      fontSize: '1.25em',
      lineHeight: '1.25em',
      margin: 0
    },
    '& .description': {
      fontSize: size === 'sm' ? '0.75em' : size === 'lg' ? '0.5em' : '.6em',
      lineHeight: '1em',
      margin: 0
    }
  }),
  handle: ({ hideHandler }) => ({
    opacity: hideHandler ? 0 : 1
  })
}))

const possiblePositions = [
  Position.Left,
  Position.Right,
  Position.Top,
  Position.Bottom
]

const normalizePosition = (pos, width, height) => {
  const { left, top } = getOvalPoint(width, height, pos)

  if (pos === 'topLeft') {
    return {
      id: pos,
      position: Position.Top,
      style: { left, top }
    }
  }
  if (pos === 'topRight') {
    return {
      id: pos,
      position: Position.Top,
      style: { left, top }
    }
  }
  if (pos === 'bottomLeft') {
    return {
      id: pos,
      position: Position.Bottom,
      style: { left, bottom: top }
    }
  }
  if (pos === 'bottomRight') {
    return {
      id: pos,
      position: Position.Bottom,
      style: { left, bottom: top }
    }
  }
  return {
    id: pos,
    position: pos
  }
}

export default function CircleNode ({ data, width, height, selected }) {
  const { state } = useEntityGraphContext()
  const canvasSettings = useMemo(() => state?.config?.canvasSettings ?? {}, [state])

  const size = useMemo(() => data?.nodeProps?.size ?? 'md', [data?.nodeProps])

  const { header, description, backgroundColor, textColor, borderColor } = useGetNodeValues(data, state.linkedEntityData)
  const style = useMemo(() => ({
    ...data.style,
    backgroundColor,
    color: textColor,
    borderColor
  }), [data.style, backgroundColor, textColor, borderColor])
  const classes = useStyles({ size, hideHandler: !!canvasSettings.hideHandlers, ...(style ?? {}) })

  const handles = useMemo(() => {
    return (data?.nodeProps?.handles ?? possiblePositions).map(pos => normalizePosition(pos, width, height))
  }, [data?.nodeProps?.handles, height, width])

  const targets = useMemo(() => {
    return (data?.nodeProps?.targets ?? possiblePositions).map(pos => normalizePosition(pos, width, height))
  }, [data?.nodeProps?.targets, height, width])

  return (
    <>
      {handles.map(({ position, style, id }) => (
        <Handle key={id} id={`handle_${id}`} type='source' position={position} style={style ?? {}} className={classes.handle} />
      ))}

      {targets.map(({ position, style, id }) => (
        <Handle key={id} id={`target_${id}`} type='target' position={position} style={style ?? {}} className={classes.handle} />
      ))}
      <div className={clsx(classes.node, { selected: selected })} style={style}>
        <p className='header'>{header}</p>
        {description && (<p className='description'>{description}</p>)}
      </div>
    </>
  )
}

CircleNode.propTypes = nodePropTypes
